import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { hasAllPermissions, hasPermission, useCheckPermissions, useTableFilter } from '@/shared';
import { useFetchSubCategorias, useUpdateSubCategorias } from '@/shared/actions/erp/compras/subcategorias-productos.actions';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import { SubCategorias } from '@/shared/interfaces';
import { useUiConfirmModalStore } from '@/store/ui';
import { PermissionsEnum } from '@/auth/interfaces';

const returnUrlSubCategoria = '/inventario/subcategoria-productos';

export interface SubcategoriasPageInterface {}

const SubcategoriasPage: React.FC<SubcategoriasPageInterface> = () => {
  useCheckPermissions(PermissionsEnum.api_add_subcategoriaproducto);

  const navigate = useNavigate();

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex } = pagination;

  ///* fetch data
  const {
    data: SubcategoriasPagingRes,
    isLoading,
    isRefetching,
  } = useFetchSubCategorias({
    page: pageIndex + 1,
    nombre: searchTerm,
  });

  ///* mutations
  const updateSubcategoriasMutation = useUpdateSubCategorias({navigate, returnUrl:returnUrlSubCategoria});
  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );
  ///* handlers
  const onEdit = (original:   SubCategorias) => {
    navigate(`${returnUrlSubCategoria}/editar/${original.id_subcategoriaproducto}`);
  };

  ///* columns
  const columns = useMemo<MRT_ColumnDef<SubCategorias>[]>(
    () => [
      {
        accessorKey: 'numero_referencia',
        header: 'Numero Referencia',
        size: 300,
      },
    /*   {
        accessorKey: 'codigo',
        header: 'Codigo',
        size: 300,
      }, */
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 300,
      },
      {
        accessorKey: 'categoria_producto',
        header: 'Categoria del producto',
        size: 300,
      },
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <SwitchComponente
            estadoSwitch={row.original.estado}
            titulo={row.original.estado ? 'Activo' : 'Inactivo'}
            setEstadoSwitch={subcategoria => {
              if (!hasPermission(PermissionsEnum.api_change_subcategoriaproducto)) return;
              setConfirmDialog({
                isOpen: true,
                title: 'Cambiar Estado',
                subtitle:
                  '¿Está seguro que desea cambiar el estado de este registro ?',
                onConfirm: () => {
                  setConfirmDialogIsOpen(false);
                  updateSubcategoriasMutation.mutate({
                    id: row.original.id_subcategoriaproducto!,
                    data: {
                      ...row.original,
                      estado: subcategoria as boolean,
                    },
                  });
                },
              });
            }}
          />
        ),
      },
    ],
    []
  );

  return (
    <SingleTableBoxScene title="Subcategorias de productos" urlRedirect={`${returnUrlSubCategoria}/crear`} enableRedirect={hasPermission(PermissionsEnum.api_add_subcategoriaproducto)}>
      <CustomSearch onChange={onChangeFilter} value={globalFilter} text="subcategorias de productos" />

      <GeneralTable<SubCategorias>
        columns={columns}
        data={SubcategoriasPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={SubcategoriasPagingRes?.data?.count}
        // // actions
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_subcategoriaproducto,
        ])}
        // crud
        onEdit={onEdit}
        canEdit={hasAllPermissions([
          PermissionsEnum.api_change_subcategoriaproducto,
        ])}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default SubcategoriasPage;
